import { useTheme } from "../../theme/useTheme"
import { MainContent } from "./MainContent"
import { Page } from "./Page"
import { PageLayout } from "./PageLayout"
import { Sidebar } from "./Sidebar"
import { SiteFooter } from "./SiteFooter"
import { TOP_NAV_HEIGHT, TopNav } from "./TopNav"
import { Box } from "@mui/material"
import React, { useState } from "react"

interface AppLayoutProps {
    children: React.ReactElement<typeof Page> | React.ReactElement<typeof Page>[]
}

export const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
    const { breakpoints } = useTheme()
    const [sidebarOverride, setSidebarOverride] = useState<boolean | null>(null)
    // the override only means something while the drawer is toggleable — on
    // large screens it must not leak across a resize
    const sidebarOpen = breakpoints.isLargeScreen ? true : (sidebarOverride ?? false)

    // entering the collapsed range always resets to drawer-closed — a stale
    // override would auto-open the drawer on every resize crossing;
    // render-time adjustment keeps the reset in the same commit
    const [wasMedium, setWasMedium] = useState(breakpoints.isMediumScreen)
    if (wasMedium !== breakpoints.isMediumScreen) {
        setWasMedium(breakpoints.isMediumScreen)
        if (breakpoints.isMediumScreen) {
            setSidebarOverride(false)
        }
    }

    const pages = React.Children.toArray(children)
    const totalPages = pages.length

    React.Children.forEach(children, (child) => {
        if (!React.isValidElement(child) || child.type !== Page) {
            throw new Error("AppLayout children must be Page components")
        }
    })

    return (
        <>
            <TopNav
                onToggleSidebar={() => setSidebarOverride(!sidebarOpen)}
                sidebarOpen={sidebarOpen}
                isMediumScreen={breakpoints.isMediumScreen}
            />
            {/* spacer for the fixed TopNav; no-print so it never blanks the
                top of a printed page */}
            <Box className="no-print" sx={{ height: TOP_NAV_HEIGHT + 1 }} />
            <Box sx={{ display: "flex" }}>
                <MainContent>
                    {pages.map((page, index) => (
                        <PageLayout
                            key={index}
                            currentPage={index + 1}
                            totalPages={totalPages}
                            isLastPage={index === totalPages - 1}
                        >
                            {page}
                        </PageLayout>
                    ))}
                    <SiteFooter />
                </MainContent>
                <Sidebar
                    open={sidebarOpen}
                    onClose={() => setSidebarOverride(false)}
                    isMediumScreen={breakpoints.isMediumScreen}
                />
            </Box>
        </>
    )
}
